/**
 * CSRF Protection Utility
 * Manages CSRF tokens for secure API requests
 */

let csrfToken = null;

/**
 * Generate a random CSRF token
 * @returns {string} CSRF token
 */
export const generateCSRFToken = () => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  const token = Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return token;
};

/**
 * Set CSRF token (usually received from backend)
 * @param {string} token - CSRF token from backend
 */
export const setCSRFToken = (token) => {
  csrfToken = token;
  // Store in sessionStorage for persistence across page reloads
  sessionStorage.setItem("csrf-token", token);
};

/**
 * Get current CSRF token
 * @returns {string|null} Current CSRF token
 */
export const getCSRFToken = () => {
  if (csrfToken) return csrfToken;

  // Try to retrieve from sessionStorage
  const storedToken = sessionStorage.getItem("csrf-token");
  if (storedToken) {
    csrfToken = storedToken;
    return csrfToken;
  }

  return null;
};

/**
 * Clear CSRF token (on logout)
 */
export const clearCSRFToken = () => {
  csrfToken = null;
  sessionStorage.removeItem("csrf-token");
};

/**
 * Initialize CSRF token by fetching from backend
 * @param {string} apiUrl - Backend URL
 * @returns {Promise<string>} CSRF token
 */
export const initializeCSRFToken = async (apiUrl) => {
  try {
    // If backend provides a CSRF token endpoint, fetch it here
    // For now, generate client-side token
    // In production, this should be fetched from: GET /api/csrf-token

    const existingToken = getCSRFToken();
    if (existingToken) return existingToken;

    // Generate new token (backend should validate this)
    const newToken = generateCSRFToken();
    setCSRFToken(newToken);
    return newToken;
  } catch (error) {
    console.error("Error initializing CSRF token:", error);
    // Generate fallback token
    const fallbackToken = generateCSRFToken();
    setCSRFToken(fallbackToken);
    return fallbackToken;
  }
};

/**
 * Validate CSRF token format
 * @param {string} token - Token to validate
 * @returns {boolean} True if valid format
 */
export const isValidCSRFToken = (token) => {
  if (!token || typeof token !== "string") return false;
  // Should be 64 character hex string
  return /^[a-f0-9]{64}$/i.test(token);
};

export default {
  generateCSRFToken,
  setCSRFToken,
  getCSRFToken,
  clearCSRFToken,
  initializeCSRFToken,
  isValidCSRFToken,
};
